import NextImage from "next/image";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";

import { currentSliderIndexAtom } from "@/states/currentSliderIndex";
import { Photos } from "@/types/common/photos";

import SimpleSlider from "../../simpleSlider";

type Props = {
  media: Photos[] | undefined;
  mediaIndex: number | undefined;
  isZendesk?: boolean;
  canRotate?: boolean;
  rotateDegree: number[];
  setRotateDegree: React.Dispatch<React.SetStateAction<number[]>>;
  goToSlide: (index: number) => void;
};

const MainSlider: React.FC<Props> = ({
  media,
  mediaIndex,
  isZendesk,
  canRotate = false,
  rotateDegree,
  setRotateDegree,
  goToSlide
}) => {
  const MAIN_SLIDER_MARGIN = 100;
  const [filteredMedia, setFilteredMedia] = useState<Photos[]>([]);
  const setCurrentSlider = useSetAtom(currentSliderIndexAtom);

  const imageDivRefs = useRef<(HTMLDivElement | null)[]>([]);

  // スワイプ処理
  const [startX, setStartX] = useState<number>(0);
  const [startY, setStartY] = useState<number>(0);
  const [endX, setEndX] = useState<number>(0);
  const [endY, setEndY] = useState<number>(0);

  /** スワイプ無視幅 */
  const MINIMUM_SWIPE_DISTANCE = 50;

  const touchStart = (e: any) => {
    setStartX(e.touches[0].pageX);
    setStartY(e.touches[0].pageY);
  };
  const touchMove = (e: any) => {
    setEndX(e.touches[0].pageX);
    setEndY(e.touches[0].pageY);
  };

  const touchEnd = (e: any) => {
    if (mediaIndex === undefined) return;
    setEndX(e.changedTouches[0].pageX);
    setEndY(e.changedTouches[0].pageY);

    const x = endX - startX;
    const y = endY - startY;

    if (Math.abs(x) < Math.abs(y)) {
      return;
    }

    if (x > MINIMUM_SWIPE_DISTANCE) {
      // 右にスワイプ
      goToSlide(mediaIndex - 1);
    } else if (x < -MINIMUM_SWIPE_DISTANCE) {
      // 左にスワイプ
      goToSlide(mediaIndex + 1);
    }
  };
  // rotateDegreeの初期化
  useEffect(() => {
    setRotateDegree(Array(media?.length).fill(0));
  }, []);

  // 画像の元のサイズ
  const [naturalSizes, setNaturalSizes] = useState<
    { width: number; height: number }[]
  >(media ? Array(media.length).fill({ width: 350, height: 400 }) : []);

  // 拡大率
  const [scales, setScales] = useState<number[]>(
    media ? Array(media.length).fill(1) : []
  );

  // 360度回転時は0度に戻す
  useEffect(() => {
    imageDivRefs.current.forEach((imageElement, index) => {
      const handleTransitionEnd = () => {
        if (rotateDegree[index] === -360) {
          setRotateDegree((prev) => {
            const newRotateDegree = [...prev];
            newRotateDegree[index] = 0;
            return newRotateDegree;
          });
        }
      };

      if (imageElement) {
        imageElement.addEventListener("transitionend", handleTransitionEnd);
        return () => {
          imageElement.removeEventListener(
            "transitionend",
            handleTransitionEnd
          );
        };
      }
    });
  }, [imageDivRefs.current]); // 依存配列をimageRefs.currentのみに変更

  // rotateDegreeの値が変更されたときにのみ再計算するためのEffect
  useEffect(() => {
    const cleanupFunctions: any[] = [];
    imageDivRefs.current.forEach((imageElement, index) => {
      const handleTransitionEnd = () => {
        if (rotateDegree[index] === -360) {
          setRotateDegree((prev) => {
            const newRotateDegree = [...prev];
            newRotateDegree[index] = 0;
            return newRotateDegree;
          });
        }
      };

      if (imageElement) {
        imageElement.addEventListener("transitionend", handleTransitionEnd);
        cleanupFunctions.push(() => {
          imageElement.removeEventListener(
            "transitionend",
            handleTransitionEnd
          );
        });
      }
    });

    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }, [rotateDegree]); // 依存配列にrotateDegreeを追加

  // メインスライダーのアイテム幅 写真の端が分かるように20pxマージンを設定
  const itemWidth = useCallback(() => {
    return window.innerWidth - 20;
  }, []);

  // 画像の拡大率を再計算
  useEffect(() => {
    setScales((prev) => {
      return prev.map((scale, index) => {
        let maxWidth = window.innerWidth - 20;
        let maxHeight = window.innerHeight - 280;

        if (!rotateDegree[index] || rotateDegree[index] % 180 === 0) {
          // 画像の横幅を　MAX横幅に合わせる
          scale = maxWidth / naturalSizes[index].width;
          let height = naturalSizes[index].height * scale;

          if (height > maxHeight) {
            scale = (scale * maxHeight) / height;
          }
        } else {
          // 横回転の場合　画像の縦をMAX横幅に合わせる
          scale = maxWidth / naturalSizes[index].height;
          let width = naturalSizes[index].width * scale;

          if (width > maxHeight) {
            scale = (scale * maxHeight) / width;
          }
        }
        return scale;
      });
    });
  }, [media, naturalSizes, rotateDegree]);

  useEffect(() => {
    // 初期値をセット
    setCurrentSlider(mediaIndex ? mediaIndex : 0);
    if (media) setFilteredMedia(media.filter((m) => m.path)); // pathが存在するものだけを抽出
  }, [media]);

  const renderRotate = (index: number) => {
    // currentSliderと一致させるためにindexを引数に渡す
    const resultRotate = rotateDegree[index];
    // 再描画する
    return resultRotate;
  };

  const rotateClass = (index: number) => {
    return renderRotate(index) === 0
      ? "rotate-0"
      : renderRotate(index) === -90
        ? "rotate-[-90deg] transition-transform duration-300"
        : renderRotate(index) === -180
          ? "rotate-[-180deg] transition-transform duration-300"
          : renderRotate(index) === -270
            ? "rotate-[-270deg] transition-transform duration-300"
            : renderRotate(index) === -360
              ? "rotate-[-360deg] transition-transform duration-300"
              : "rotate-[-360deg] transition-transform duration-300";
  };

  // モーダルメイン画像の高さを再計算
  const calcImageWrapperSize = useCallback(
    (e: { naturalWidth: number; naturalHeight: number }, index: number) => {
      setNaturalSizes((prev) => {
        return prev.map((size, idx) => {
          if (idx === index) {
            return { width: e.naturalWidth, height: e.naturalHeight };
          }
          return size;
        });
      });
    },
    []
  );

  // スマホ縦横回転など、画面サイズ変更時に再計算
  window.onresize = () => {
    naturalSizes.forEach((size, index) => {
      calcImageWrapperSize(
        { naturalWidth: size.width, naturalHeight: size.height },
        index
      );
    });
  };

  return (
    <SimpleSlider
      margin={MAIN_SLIDER_MARGIN}
      itemWidth={itemWidth()}
      currentIndex={mediaIndex}
    >
      <div
        className={`flex transition-all duration-500`}
        style={{
          transform: `translateX(-${
            (itemWidth() + 20) * (mediaIndex ? mediaIndex : 0)
          }px)`,
          gap: `${MAIN_SLIDER_MARGIN}px`
        }}
        onTouchStart={(e) => touchStart(e)}
        onTouchMove={(e) => touchMove(e)}
        onTouchEnd={(e) => touchEnd(e)}
      >
        {filteredMedia.map((medium, index) => {
          return (
            <div
              className={clsx(
                "w-full",
                canRotate ? "items-center" : "items-end"
              )}
              key={medium.id}
            >
              {/* メイン写真表示可能エリア */}
              <div
                className={`flex h-[calc(100vh-280px)] items-end justify-center`}
                style={{ width: `${itemWidth()}px` }}
              >
                <div
                  ref={(el) => (imageDivRefs.current[index] = el)}
                  className={clsx(rotateClass(index))}
                >
                  {/* メイン画像枠 */}
                  <div
                    style={{
                      height: `${naturalSizes[index].height * scales[index]}px`,
                      width: `${naturalSizes[index].width * scales[index]}px`,
                      transition: "all 0.3s ease"
                    }}
                  >
                    <NextImage
                      src={
                        isZendesk
                          ? medium.path
                          : `${process.env.APP_PHOTO_URL}/${medium.path}`
                      }
                      alt=""
                      layout="fill"
                      objectFit="contain"
                      className="image"
                      onLoadingComplete={(img) => {
                        calcImageWrapperSize(img, index);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SimpleSlider>
  );
};

export default MainSlider;
